/**
 * MessageAttachmentRenderer (#111 P4b — bounded slice).
 *
 * MIME-typed renderer registry for attachments inline inside a chat
 * message. Switches on `mimeType` and falls through to a download chip
 * for anything unknown so we never silently drop an attachment.
 *
 * Renderers, in order:
 *   • image/*           — inline `<img>` capped at 320px tall, click to
 *                          open the full file in a new tab.
 *   • application/pdf   — embedded `<iframe>` capped at 480px tall,
 *                          fallback download link if the browser blocks
 *                          inline PDFs.
 *   • audio/*           — native `<audio controls>` (Safari + Chromium
 *                          handle MP3/WAV/OGG without polyfill).
 *   • video/*           — native `<video controls>` capped at 360px tall.
 *   • text/* + json     — preview the first 1k chars in a code block.
 *   • everything else   — download chip with file icon, name, size.
 *
 * Not addressed in this slice (still open in #111 P4b):
 *   • Multi-party member-list sidebar / @-mention picker (separate UI)
 *   • Backend wiring to populate `Message.attachments` from the chat
 *     API response — this component is ready, but `useChatPanel` and
 *     the api-server's chat handler need to surface attachment URLs
 *     through the wire.
 */

import { FileText, Download, FileQuestion } from "lucide-react"
import type { MessageAttachment } from "@/types/chat.types"
import { cn } from "@/lib/utils"

interface MessageAttachmentRendererProps {
  attachments: MessageAttachment[]
  className?: string
}

function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return ""
  const units = ["B", "KB", "MB", "GB"]
  let n = bytes
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i += 1
  }
  return `${n < 10 ? n.toFixed(1) : Math.round(n)} ${units[i]}`
}

function ImageAttachment({ a }: { a: MessageAttachment }) {
  return (
    <a
      href={a.url}
      target="_blank"
      rel="noreferrer noopener"
      className="block overflow-hidden rounded-lg border border-border bg-muted/30"
      title={a.fileName}
    >
      <img
        src={a.thumbnailUrl ?? a.url}
        alt={a.fileName}
        loading="lazy"
        className="max-h-80 w-auto object-contain"
      />
    </a>
  )
}

function PdfAttachment({ a }: { a: MessageAttachment }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
      <iframe
        src={a.url}
        title={a.fileName}
        className="block h-[480px] w-full"
      />
      <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/40 px-3 py-1.5">
        <span className="truncate text-xs text-muted-foreground">{a.fileName}</span>
        <a
          href={a.url}
          target="_blank"
          rel="noreferrer noopener"
          className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <Download className="size-3" />
          <span>Open</span>
        </a>
      </div>
    </div>
  )
}

function AudioAttachment({ a }: { a: MessageAttachment }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-2">
      <audio controls className="w-full" src={a.url}>
        <a href={a.url} target="_blank" rel="noreferrer noopener">
          {a.fileName}
        </a>
      </audio>
      <div className="mt-1 truncate text-[11px] text-muted-foreground">{a.fileName}</div>
    </div>
  )
}

function VideoAttachment({ a }: { a: MessageAttachment }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
      <video
        controls
        className="block max-h-[360px] w-full bg-black"
        src={a.url}
      >
        <a href={a.url} target="_blank" rel="noreferrer noopener">
          {a.fileName}
        </a>
      </video>
    </div>
  )
}

function DownloadChip({ a, icon }: { a: MessageAttachment; icon: React.ReactNode }) {
  return (
    <a
      href={a.url}
      target="_blank"
      rel="noreferrer noopener"
      className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-foreground hover:border-border/80 hover:bg-muted/50"
    >
      {icon}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">{a.fileName}</span>
        {a.sizeBytes != null && (
          <span className="truncate text-[10px] text-muted-foreground">
            {formatBytes(a.sizeBytes)}
          </span>
        )}
      </div>
      <Download className="size-3 shrink-0 text-muted-foreground" />
    </a>
  )
}

function renderOne(a: MessageAttachment): React.ReactNode {
  const mime = (a.mimeType ?? "").toLowerCase()
  if (mime.startsWith("image/")) return <ImageAttachment a={a} />
  if (mime === "application/pdf") return <PdfAttachment a={a} />
  if (mime.startsWith("audio/")) return <AudioAttachment a={a} />
  if (mime.startsWith("video/")) return <VideoAttachment a={a} />
  if (mime.startsWith("text/") || mime === "application/json") {
    // Text/JSON — render as a download chip; full inline-preview view
    // would need fetching the body, which we intentionally don't do
    // from this renderer (would defeat the lazy-load posture).
    return <DownloadChip a={a} icon={<FileText className="size-4 shrink-0 text-muted-foreground" />} />
  }
  return (
    <DownloadChip a={a} icon={<FileQuestion className="size-4 shrink-0 text-muted-foreground" />} />
  )
}

export function MessageAttachmentRenderer({
  attachments,
  className,
}: MessageAttachmentRendererProps) {
  if (!attachments || attachments.length === 0) return null
  return (
    <div className={cn("mt-2 flex flex-col gap-2", className)}>
      {attachments.map((a) => (
        <div key={a.id}>{renderOne(a)}</div>
      ))}
    </div>
  )
}
